import {ShapeLineStyle, ShapeType, ShapeWidgetData} from '@dv5/domain'
import React, {CSSProperties, FC} from 'react'
import {ColorPicker} from '../common/ColorPicker'
import {SHAPE_COLORS} from "./constants"
import {ShapePicker} from "./ShapePicker"
import {useShapeWidgetUpdate} from "./useShapeWidgetUpdate"
import {ShapeLineStylePicker} from './ShapeLineStylePicker'
import './ShapeWidgetToolbar.less'

interface Props {
    canvasId: string
    widgetId: string
    data: ShapeWidgetData
    toolbarStyles: CSSProperties
    onExpandToFull: () => void
}

export const ShapeWidgetToolbar: FC<Props> = ({
    canvasId,
    widgetId,
    data,
    toolbarStyles,
    onExpandToFull,
}) => {
    const { updateData } = useShapeWidgetUpdate(canvasId, widgetId, data)

    const handleFillColorChange = (color: string) => {
        updateData(['bgColor'], {bgColor: color})
    }

    const handleBorderColorChange = (color: string) => {
        updateData(['borderColor'], {borderColor: color})
    }

    const handleShapeTypeChange = (shapeType: ShapeType) => {
        updateData(['shapeType'], {shapeType})
    }

    const handleLineStyleChange = (lineStyle: ShapeLineStyle) => {
        updateData(['lineStyle'], {lineStyle})
    }

    const handleBorderWidthChange = (event: React.MouseEvent<HTMLButtonElement>, delta: number) => {
        event.stopPropagation()
        event.preventDefault()
        const newWidth = Math.max(0, Math.min(12, (data.borderWidth != null ? data.borderWidth : 1) + delta))
        updateData(['borderWidth'], {borderWidth: newWidth})
    }
    const changeExpand = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation()
        event.preventDefault()
        if (onExpandToFull) {
            onExpandToFull()
        }
    }

    return (
        <div className='shape-toolbar' style={toolbarStyles}>
            <ShapePicker
                prefix="shape-toolbar"
                value={data.shapeType}
                onChange={handleShapeTypeChange}
            />
            <div className='shape-toolbar__divider' />
            <ColorPicker
                value={data.bgColor}
                presets={SHAPE_COLORS}
                onChange={handleFillColorChange}
            />
            <div className='shape-toolbar__divider' />
            <ColorPicker
                value={data.borderColor}
                presets={SHAPE_COLORS}
                onChange={handleBorderColorChange}
            />
            <div className='shape-toolbar__divider' />
            <ShapeLineStylePicker value={data.lineStyle} onChange={handleLineStyleChange} />
            <div className='shape-toolbar__divider' />
            <div className='shape-toolbar__border-width'>
                <button
                    className='shape-toolbar__button'
                    onMouseDown={event => handleBorderWidthChange(event, -1)}
                    aria-label='Decrease border width'
                    type='button'
                    disabled={(data.borderWidth != null ? data.borderWidth : 1) <= 0}
                >
                    −
                </button>
                <span className='shape-toolbar__border-value'>{data.borderWidth != null ? data.borderWidth : 1}</span>
                <button
                    className='shape-toolbar__button'
                    onMouseDown={event => handleBorderWidthChange(event, 1)}
                    aria-label='Increase border width'
                    type='button'
                    disabled={(data.borderWidth != null ? data.borderWidth : 1) >= 12}
                >
                    +
                </button>
            </div>
            <div className='shape-toolbar__divider' />
            <button
                className='shape-toolbar__button shape-toolbar__button--expand'
                onMouseDown={changeExpand}
                aria-label='Expand to full editor'
                type='button'
            >
                ↗
            </button>
        </div>
    )
}
